/**
 * <SectorMap> — S&P500 섹터 히트맵 (Finviz 스타일 treemap)
 *
 * Select Sector SPDR 11개 ETF 기반.
 *   XLK Technology · XLF Financials · XLV Health Care · XLY Consumer Disc.
 *   XLC Communication · XLI Industrials · XLP Consumer Staples · XLE Energy
 *   XLU Utilities · XLRE Real Estate · XLB Materials
 *
 * 레이아웃: 6×4 CSS grid-template-areas (비중 반영 · 고정 배치)
 *   XLK(3×2) | XLF(3×2)
 *   XLV(2×1) | XLY(2×1) | XLC(2×1)
 *   XLI | XLP | XLE | XLU | XLRE | XLB
 *
 * 색: 변동률에 직접 비례한 color-mix (±3% 에서 최대 intensity).
 * 테두리 없음 · gap 2px · flat heatmap.
 *
 * 데이터: GET /api/ticker/market/sectors
 */
"use client";

import { useEffect, useRef, useState } from "react";
import type { WidgetMeta, WidgetProps } from "../types";

interface SectorItem {
  ticker: string;
  nameKo: string;
  nameEn: string;
  weight: number;
  price: number | null;
  changePercent: number | null;
  change: number | null;
  stale?: boolean;
}
interface SectorsData {
  updatedAt: string;
  items: SectorItem[];
  stale?: boolean; // true = 전부 장 시작 전/정규장 종료 후 (전일 종가 기준)
}

const REFRESH_MS = 60 * 1000;

/**
 * 고정 treemap 배치 — S&P500 비중을 grid cells 개수로 반영
 *   XLK 31%  → 6 cells (3×2)
 *   XLF 13%  → 6 cells (3×2)
 *   XLV/XLY/XLC  → 2 cells (2×1)
 *   나머지 6개 → 1 cell (1×1)
 * 총 24 cells = 6 cols × 4 rows
 */
const LAYOUT = [
  { ticker: "XLK",  tier: "xl" as const },
  { ticker: "XLF",  tier: "xl" as const },
  { ticker: "XLV",  tier: "md" as const },
  { ticker: "XLY",  tier: "md" as const },
  { ticker: "XLC",  tier: "md" as const },
  { ticker: "XLI",  tier: "sm" as const },
  { ticker: "XLP",  tier: "sm" as const },
  { ticker: "XLE",  tier: "sm" as const },
  { ticker: "XLU",  tier: "sm" as const },
  { ticker: "XLRE", tier: "sm" as const },
  { ticker: "XLB",  tier: "sm" as const },
];

/**
 * 변동률 → 배경색 (CSS color-mix string).
 * ±0.25% 미만: 무채색(gray-100)
 * 그 외: ±3% 에서 80% intensity 가 최대 · 그 사이 선형 보간
 */
function tileBackground(pct: number | null): string {
  if (pct == null || Math.abs(pct) < 0.0015) {
    return "var(--bs-gray-100)";
  }
  const intensity = Math.min(Math.abs(pct) / 0.03, 1);
  const mixPct = Math.round(15 + intensity * 65); // 15% ~ 80%
  const base = pct > 0 ? "var(--bs-up)" : "var(--bs-down)";
  return `color-mix(in srgb, ${base} ${mixPct}%, var(--bs-paper))`;
}

/** 배경이 진할 때 (intensity > 0.55) 글자를 paper 색으로 반전 */
function isStrongTint(pct: number | null): boolean {
  if (pct == null) return false;
  const intensity = Math.min(Math.abs(pct) / 0.03, 1);
  return intensity > 0.55;
}

function formatPct(pct: number | null): string {
  if (pct == null || !Number.isFinite(pct)) return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${(pct * 100).toFixed(2)}%`;
}

export function SectorMap(_props: WidgetProps) {
  const [data, setData] = useState<SectorsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/sectors", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) {
            setError("로드 실패");
            setLoading(false);
          }
          return;
        }
        const d = (await res.json()) as SectorsData;
        if (mountedRef.current) {
          setData(d);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) {
          setError("네트워크 오류");
          setLoading(false);
        }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, []);

  // 티커별 맵 (LAYOUT 순서대로 렌더 · 고정 배치 유지)
  const byTicker: Record<string, SectorItem> = {};
  for (const it of data?.items ?? []) byTicker[it.ticker] = it;

  // 상단 요약 — 상승/하락/평균
  const items = data?.items ?? [];
  const upCount = items.filter((x) => (x.changePercent ?? 0) > 0).length;
  const downCount = items.filter((x) => (x.changePercent ?? 0) < 0).length;
  const avg =
    items.length > 0
      ? items.reduce((s, x) => s + (x.changePercent ?? 0), 0) / items.length
      : null;

  return (
    <div className="dw-body dw-sm">
      <div className="dw-head">
        <span className="dw-title">섹터 맵</span>
        {items.length > 0 && (
          <span className="dw-sm-summary">
            <b className={upCount > downCount ? "up" : ""}>▲ {upCount}</b>
            <b className={downCount > upCount ? "down" : ""}>▼ {downCount}</b>
            {avg != null && (
              <b className={avg > 0 ? "up" : avg < 0 ? "down" : ""}>
                평균 {formatPct(avg)}
              </b>
            )}
          </span>
        )}
      </div>

      {error ? (
        <div className="dw-sm-error">{error}</div>
      ) : (
        <div className="dw-sm-grid">
          {LAYOUT.map(({ ticker, tier }) => {
            const it = byTicker[ticker];
            const pct = it?.changePercent ?? null;
            const strong = isStrongTint(pct);
            return (
              <div
                key={ticker}
                className={`dw-sm-tile tier-${tier} ${strong ? "is-strong" : ""}`}
                style={{ gridArea: ticker, background: tileBackground(pct) }}
                title={
                  it
                    ? `${it.nameKo} · ${it.nameEn} · ${it.ticker} · S&P 비중 ${it.weight.toFixed(1)}%`
                    : ticker
                }
              >
                {loading && !it ? (
                  <span className="bs-skel dw-sm-tile-skel" />
                ) : (
                  <>
                    <span className="dw-sm-tile-name">{it?.nameKo ?? ticker}</span>
                    <span className="dw-sm-tile-pct">{formatPct(pct)}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {data?.updatedAt && (
        <div className="dw-sm-footer">
          {new Date(data.updatedAt).toLocaleTimeString("ko-KR", {
            hour: "2-digit",
            minute: "2-digit",
          })}{" "}
          기준 · Select Sector SPDR
          {data.stale && " · 장 시작 전 · 전일 종가"}
        </div>
      )}
    </div>
  );
}

export const sectorMapMeta: WidgetMeta = {
  type: "sector-map",
  name: "섹터 맵",
  description: "S&P500 11개 섹터 히트맵 (SPDR ETF)",
  icon: "🗺️",
  category: "market",
  defaultSize: { w: 8, h: 4 },
  availableSizes: [
    { w: 6, h: 4 },
    { w: 8, h: 4 },
    { w: 12, h: 4 },
  ],
  minW: 4,
  maxW: 12,
  minH: 3,
  maxH: 6,
  component: SectorMap,
};
